// ---------------------------------------------------------------------------
// O SITE PÚBLICO COMO DADO.
//
// Três níveis, e só dois deles têm página:
//
//   <organizacao>.alfredo.food   a REDE — links para as landings das marcas
//   <company>.alfredo.food       a MARCA — cardápio, unidades e "peça agora"
//   a unidade                    NÃO tem subdomínio: é DESTINO de pedido,
//                                escolhida dentro da landing, por proximidade
//
// ATENÇÃO — este arquivo é COMPARTILHADO por três repositórios:
//   alfredo-central-backend/src/comum/site   (canônica — o dono do dado)
//   alfredo-central-web/src/comum/site       (o editor)
//   alfredo-pedidos/src/comum/site           (quem renderiza para o cliente)
// As três cópias precisam ser BYTE A BYTE iguais: é isso que garante que o
// preview do editor não minta sobre a landing que o cliente do restaurante vê.
// Quem edita altera a cópia do alfredo-central-backend e roda:
//   node ferramentas/sincronizarSite.js
// O build de cada repo confere o hash (ferramentas/verificarSite.js) e QUEBRA
// se as cópias divergirem.
//
// Sem dependências de propósito: nada de Prisma, React, Zod. Só TypeScript
// puro, para rodar no Fastify, no navegador e no Server Component do Next.
// ---------------------------------------------------------------------------

/** Quem é dono de uma página. A unidade nunca aparece aqui — ver o cabeçalho. */
export type EscopoDoSite = "organization" | "company";

export const ESCOPOS_DO_SITE: EscopoDoSite[] = ["organization", "company"];

/**
 * A cor da marca do restaurante, em tokens fechados.
 *
 * LISTA FECHADA, nunca CSS livre. O Raio aceita `customCss` e id de pixel
 * injetados com `dangerouslySetInnerHTML`; aqui a MESMA ORIGEM tem a tela onde
 * o número do cartão é digitado, então conteúdo do dono não vira código.
 *
 * E a cor de AÇÃO continua a do sistema (verde). O contraste do par
 * verde/verde-texto é calibrado para AA; se o dono escolhesse a cor do botão de
 * pagar, o contraste quebraria sem aviso. A cor dele pinta capa, títulos e
 * detalhes — não o botão que cobra.
 */
export interface TemaDoSite {
  versao: 1;
  /** Cor da marca, em hexadecimal `#rrggbb`. Capa, títulos e detalhes. */
  marca: string;
  /** Texto sobre a cor da marca. Só claro ou escuro: o contraste é conferido. */
  marcaTexto: "claro" | "escuro";
  /** Fundo da capa quando não há imagem. */
  marcaFundo: string;
  /** Cantos: o quanto a marca é "dura" ou "macia". */
  cantos: "reto" | "suave" | "redondo";
  /** Família do título. Lista fechada — não há upload de fonte. */
  tipografia: "moderna" | "classica" | "manuscrita";
}

export const TEMA_PADRAO: TemaDoSite = {
  versao: 1,
  marca: "#1c7295",
  marcaTexto: "claro",
  marcaFundo: "#f7f8f7",
  cantos: "suave",
  tipografia: "moderna",
};

/** Um bloco da página, já validado. `config` é o objeto do descritor do tipo. */
export interface BlocoDoSite {
  blocoUuid: string;
  tipo: string;
  ordem: number;
  config: Record<string, unknown>;
}

export interface PaginaDoSite {
  paginaUuid: string;
  escopo: EscopoDoSite;
  escopoUuid: string;
  caminho: string;
  titulo: string;
  descricao: string | null;
  imagemOg: string | null;
  estado: "rascunho" | "publicada" | "arquivada";
}

/**
 * Uma unidade como a landing precisa dela: para MOSTRAR (endereço, telefone,
 * mapa) e para ESCOLHER (coordenada, distância, se aceita pedido).
 *
 * `lat`/`lng` vêm nulos quando a coordenada ainda não foi CONFERIDA POR GENTE.
 * Não é preciosismo: o endereço gravado da Lucco é de RUA sem número, e a
 * coordenada derivada dele erra centenas de metros. Unidade sem coordenada
 * conferida aparece na lista, mas não entra na ordenação por distância — errar
 * a unidade mais próxima é um erro silencioso.
 */
export interface UnidadeDaLanding {
  subsidiaryUuid: string;
  nome: string;
  endereco: string | null;
  telefone: string | null;
  whatsapp: string | null;
  horarios: FaixaDeHorario[];
  lat: number | null;
  lng: number | null;
  /** Falso enquanto não houver vínculo provado com um caixa — ver a §5.5. */
  aceitaPedido: boolean;
}

export interface FaixaDeHorario {
  /** 0 = domingo … 6 = sábado, como `Date.getDay()`. */
  dias: number[];
  de: string;
  ate: string;
}

/** A marca dona da página, para cabeçalho, rodapé e dados estruturados. */
export interface MarcaDaLanding {
  escopo: EscopoDoSite;
  escopoUuid: string;
  nome: string;
  logoUrl: string | null;
  /** Só em escopo `company`: a rede a que ela pertence. */
  redeNome: string | null;
  /** Só em escopo `organization`: as marcas da rede, com handle. */
  marcas: MarcaDaRede[];
}

export interface MarcaDaRede {
  companyUuid: string;
  nome: string;
  logoUrl: string | null;
  handle: string | null;
  /** Quantas unidades a marca tem — o cartão da rede mostra. */
  unidades: number;
}

/** O que a rota pública devolve: tudo que a landing precisa, numa chamada só. */
export interface SitePublico {
  handle: string;
  marca: MarcaDaLanding;
  tema: TemaDoSite;
  pagina: PaginaDoSite;
  blocos: BlocoDoSite[];
  unidades: UnidadeDaLanding[];
}

/** Resolução de handle. `redirecionarPara` preenchido = handle aposentado. */
export interface ResolucaoDeHandle {
  escopo: EscopoDoSite;
  escopoUuid: string;
  vigente: boolean;
  redirecionarPara: string | null;
}
